import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { PageHeader } from "@/components/layout/heading";
import { ResponsiveMascot } from "@/components/animations/mascot";
import { ProductCard } from "@/components/card/product";
import { Skeleton } from "@/components/ui/skeleton";
import { KnowledgeApiFactory } from "@/apis/knowledge";
import { Product } from "@/schema/knowledge/products";
import { useEffect, useRef } from "react";
import { useInView } from "motion/react";

export const Route = createFileRoute("/dashboard/_layout/products")({
  component: RouteComponent,
});

const mContentStyle = {
  minHeight: "calc(100dvh - 10rem)",
} as React.CSSProperties;

const mPageSize = 20;

function RouteComponent() {
  const {
    data: products = [],
    status,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: KnowledgeApiFactory.Keys.FetchInfiniteBrandProducts({}),
    queryFn: ({ pageParam }) => KnowledgeApiFactory.Apis.FetchBrandProducts({ page: pageParam, size: mPageSize }),
    getNextPageParam: (page) => (page.page < page.pages ? page.page + 1 : null),
    select: (data) => data.pages.flatMap((page) => page.items),
  });

  switch (status) {
    case "pending": {
      return (
        <section id="products">
          <PageHeader title="Products" description="Manage your products and their related data" />
          <div style={mContentStyle} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-8">
            {Array.from({ length: 9 }).map((_, index) => (
              <Skeleton key={index} className="h-96 w-full" />
            ))}
          </div>
        </section>
      );
    }

    case "error": {
      return (
        <section id="products">
          <PageHeader title="Products" description="Manage your products and their related data" />
          <div style={mContentStyle} className="flex flex-col items-center justify-center h-full w-full mt-8">
            <ResponsiveMascot mascot="MascotError" className="size-64 md:size-96 relative mx-auto -top-2 -right-2.5 md:right-3.5" />
            <p className="text-lg font-medium text-foreground -mt-10 mb-1">Error fetching products</p>
            <p className="text-sm text-muted-foreground mt-1">Please try again later.</p>
          </div>
        </section>
      );
    }

    default: {
      return (
        <section id="products">
          <PageHeader title="Products" description="Manage your products and their related data" />
          <div className="mt-8">
            {!products.length ? (
              <ProductsGrid products={products} canLoadMore={hasNextPage} onLoadMore={fetchNextPage} />
            ) : (
              <div className="flex flex-col items-center justify-center h-full w-full" style={mContentStyle}>
                <ResponsiveMascot mascot="MascotError" className="size-64 md:size-80 relative mx-auto -top-2 -right-2.5 md:right-3.5" />
                <p className="text-lg font-medium text-foreground -mt-10 mb-1">No products found</p>
                <p className="text-sm text-muted-foreground">Please add your products to get started.</p>
              </div>
            )}
          </div>
        </section>
      );
    }
  }
}

interface ProductsGridProps {
  products: Product[];
  canLoadMore: boolean;
  onLoadMore: () => void;
}
function ProductsGrid({ products, canLoadMore, onLoadMore }: ProductsGridProps) {
  const ref = useRef<HTMLDivElement>(null!);

  const visible = useInView(ref);

  useEffect(() => {
    if (!visible || !canLoadMore) return;
    onLoadMore();
  }, [visible, canLoadMore, onLoadMore]);

  return (
    <div className="relative">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
      <div ref={ref} className="flex items-center justify-center gap-2.5 pt-12 pb-4">
        {canLoadMore ? <div className="spinner size-5 !border-primary" /> : null}
        <span className="text-sm font-medium text-muted-foreground">{canLoadMore ? "Loading more products..." : "End of results"}</span>
      </div>
    </div>
  );
}
